/**
 * OutstandingRequestsPanel — right column of the dashboard's two-up row.
 *
 * Per Figma 562:22120: header (title + description) above two stat rows
 * (Reimbursements, Personal Expenses) separated by a divider. Each row shows a
 * brand-tinted avatar icon, the category name, a total count, the arrow to the
 * list it counts, and the per-status breakdown indented beneath it.
 */
import React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  MoneyAdd01Icon,
  MoneySend01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { Inline } from "components/layout/reusableLayouts";
import { Avatar, Card, DsIcon, IconButton } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { RequestStatusCounts } from "hooks/useEmployeeDashboard";
import { shownCount } from "utils/transactions/requestCounts";
import { OUTSTANDING_PERSONAL_STATUSES } from "utils/transactions/transactionGroups";
import { myTransactionsLink } from "utils/transactions/myTransactionsLink";

export interface OutstandingRequestsPanelProps {
  reimbursements: RequestStatusCounts;
  personalExpenses: RequestStatusCounts;
  /** Without the feature there is no Reimbursements page to open, as in the sidebar. */
  showReimbursements: boolean;
}

interface RequestRowProps {
  icon: typeof MoneyAdd01Icon;
  name: string;
  totalLabel: string;
  statuses: string[];
  link?: { to: string; label: string };
}

const RequestRow: React.FC<RequestRowProps> = ({
  icon,
  name,
  totalLabel,
  statuses,
  link,
}) => (
  <Stack
    sx={{
      gap: "var(--space-3)",
      flex: 1,
      justifyContent: "center",
    }}
  >
    <Inline gap="var(--space-4)" align="center" justify="between">
      <Inline gap="var(--space-4)" align="center">
        <Avatar
          size="md"
          color="brand"
          icon={<HugeiconsIcon icon={icon} size={16} />}
        />
        <Typography variant="h6">{name}</Typography>
        <Typography
          variant="subtitle2"
          sx={{
            color: "var(--color-text-secondary)",
          }}
        >
          {totalLabel}
        </Typography>
      </Inline>
      {link && (
        // IconButton does not type its `component`'s props, so the router's `to` rides in a spread.
        <IconButton
          component={Link}
          {...{ to: link.to }}
          variant="outlined"
          aria-label={link.label}
        >
          <DsIcon name="arrowRight" size={18} />
        </IconButton>
      )}
    </Inline>
    <Stack
      sx={{
        gap: "var(--space-3)",
        paddingLeft: "36px",
      }}
    >
      {statuses.map((status) => (
        <Typography
          key={status}
          variant="body2"
          sx={{
            color: "var(--color-text-tertiary)",
          }}
        >
          {status}
        </Typography>
      ))}
    </Stack>
  </Stack>
);

const OutstandingRequestsPanel: React.FC<OutstandingRequestsPanelProps> = ({
  reimbursements,
  personalExpenses,
  showReimbursements,
}) => {
  const { t } = useTranslation();

  const statusLines = ({ pending, processing }: RequestStatusCounts) => [
    t("{{count}} Outstanding", { count: pending }),
    t("{{count}} Processing", { count: processing }),
  ];

  return (
    <Card
      variant="outlined"
      title={t("Outstanding Requests")}
      subtitle={t("Track reimbursements and personal expenses")}
    >
      <RequestRow
        icon={MoneyAdd01Icon}
        name={t("Reimbursements")}
        totalLabel={t(
          reimbursements.total === 1
            ? "{{count}} Request"
            : "{{count}} Requests",
          { count: shownCount(reimbursements) },
        )}
        statuses={statusLines(reimbursements)}
        link={
          showReimbursements
            ? { to: "/my/reimbursements", label: t("View reimbursements") }
            : undefined
        }
      />

      <Divider />

      <RequestRow
        icon={MoneySend01Icon}
        name={t("Personal Expenses")}
        totalLabel={t(
          personalExpenses.total === 1
            ? "{{count}} Expense"
            : "{{count}} Expenses",
          { count: shownCount(personalExpenses) },
        )}
        statuses={statusLines(personalExpenses)}
        link={{
          to: myTransactionsLink({ statuses: OUTSTANDING_PERSONAL_STATUSES }),
          label: t("View personal expenses"),
        }}
      />
    </Card>
  );
};

export default OutstandingRequestsPanel;
